import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useToast, StatusBadge, formatDate, useIsAdmin } from '../components/shared';

export default function DeviceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [device, setDevice] = useState(null);
  const [showTicketForm, setShowTicketForm] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [ticketForm, setTicketForm] = useState({ fault_description: '', notes: '', parent_ticket_id: '', received_at: today });
  const [loading, setLoading] = useState(false);
  const isAdmin = useIsAdmin();

  useEffect(() => {
    api.getDevice(id).then(setDevice).catch(() => navigate('/devices'));
  }, [id, navigate]);

  async function createTicket() {
    setLoading(true);
    try {
      const t = await api.createTicket({
        device_id: id,
        fault_description: ticketForm.fault_description,
        notes: ticketForm.notes,
        parent_ticket_id: ticketForm.parent_ticket_id || null,
        received_at: ticketForm.received_at ? ticketForm.received_at + 'T00:00:00.000Z' : null,
      });
      toast('Ticket created');
      navigate(`/tickets/${t.id}`);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  if (!device) return <div className="page"><div className="empty">Loading...</div></div>;

  return (
    <div className="page">
      <span className="back-btn" onClick={() => navigate('/devices')}>← Devices</span>

      <div className="detail-header">
        <div className="flex items-center gap-12">
          <h1 className="page-title">{device.name}</h1>
          {device.alias && <span className="badge badge-gray">{device.alias}</span>}
        </div>
        <div className="detail-meta">
          <div className="detail-meta-item">
            <div className="key">Serial</div>
            <div className="val">{device.serial}</div>
          </div>
          <div className="detail-meta-item">
            <div className="key">Added</div>
            <div className="val">{formatDate(device.created_at)}</div>
          </div>
          <div className="detail-meta-item">
            <div className="key">Total Tickets</div>
            <div className="val">{device.tickets?.length || 0}</div>
          </div>
        </div>
        {device.notes && <p className="text-sm text-muted mt-8">{device.notes}</p>}
      </div>

      <div className="flex items-center gap-8 mb-16">
        <div className="section-title" style={{ flex: 1 }}>Repair History</div>
        {isAdmin && <button className="btn btn-primary btn-sm" onClick={() => setShowTicketForm(v => !v)}>
          {showTicketForm ? 'Cancel' : '+ New Ticket'}
        </button>}
      </div>

      {showTicketForm && (
        <div className="card mb-16">
          <div className="grid-2">
            <div className="field">
              <label>Fault Description</label>
              <input className="input" value={ticketForm.fault_description}
                onChange={e => setTicketForm(f => ({ ...f, fault_description: e.target.value }))}
                placeholder="What's wrong with the device?" />
            </div>
            <div className="field">
              <label>Received Date</label>
              <input type="date" className="input" value={ticketForm.received_at}
                onChange={e => setTicketForm(f => ({ ...f, received_at: e.target.value }))} />
            </div>
          </div>
          <div className="field">
            <label>Linked to previous ticket (return)?</label>
            <select className="select" value={ticketForm.parent_ticket_id}
              onChange={e => setTicketForm(f => ({ ...f, parent_ticket_id: e.target.value }))}>
              <option value="">No — new issue</option>
              {device.tickets?.map(t => (
                <option key={t.id} value={t.id}>
                  #{t.id.slice(0, 8)} — {t.fault_description || 'No description'} ({formatDate(t.received_at)})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label>Notes</label>
            <textarea className="input" value={ticketForm.notes}
              onChange={e => setTicketForm(f => ({ ...f, notes: e.target.value }))}
              rows={2} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={createTicket} disabled={loading}>
            {loading ? 'Creating...' : 'Create Ticket'}
          </button>
        </div>
      )}

      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Ticket ID</th>
              <th>Fault</th>
              <th>Status</th>
              <th>Parts</th>
              <th>Invoices</th>
              <th>Received</th>
              <th>Delivered</th>
              <th>Return</th>
            </tr>
          </thead>
          <tbody>
            {(!device.tickets || device.tickets.length === 0) ? (
              <tr><td colSpan={8}><div className="empty">No repair history</div></td></tr>
            ) : device.tickets.map(t => (
              <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/tickets/${t.id}`)}>
                <td><span className="mono text-sm">{t.id.slice(0, 8)}...</span></td>
                <td className="text-sm text-muted">{t.fault_description || '—'}</td>
                <td><StatusBadge status={t.status} /></td>
                <td><span className="mono text-sm">{t.parts_count}</span></td>
                <td><span className="mono text-sm">{t.invoice_count}</span></td>
                <td className="mono text-sm text-dim">{formatDate(t.received_at)}</td>
                <td className="mono text-sm text-dim">{formatDate(t.delivered_at)}</td>
                <td>{t.parent_ticket_id ? <span className="badge badge-red">↩ Return</span> : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
